// =============================================================================
// DateNavigator - Navigazione data con pulsanti prev/next/oggi
// =============================================================================

import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../../hooks/useDateLocale';
import { MiniCalendar } from './MiniCalendar';
import type { DateNavigatorProps } from '../types/scheduler.types';

export function DateNavigator({ date, label, onDateChange, onPrev, onNext, onToday }: DateNavigatorProps) {
  const { t } = useTranslation('scheduler');
  const dateLocale = useDateLocale();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleCalendarSelect = useCallback((d: Date) => {
    onDateChange(d);
  }, [onDateChange]);

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1">
        <button
          onClick={onPrev}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={label ? t('nav.previousPeriod') : t('nav.previousDay')}
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={onToday}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {t('nav.today')}
        </button>
        <button
          onClick={onNext}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={label ? t('nav.nextPeriod') : t('nav.nextDay')}
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>
      
      <div className="relative flex items-center gap-2">
        <Calendar className="w-5 h-5 text-gray-400" />
        <button
          onClick={() => setCalendarOpen(!calendarOpen)}
          className="flex items-center gap-1.5 text-lg font-semibold text-gray-900 hover:text-gray-700 transition-colors capitalize"
          title={t('nav.selectDate')}
        >
          {label ?? format(date, 'EEEE d MMMM yyyy', { locale: dateLocale })}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${calendarOpen ? 'rotate-180' : ''}`} />
        </button>
        {calendarOpen && (
          <MiniCalendar
            selectedDate={date}
            onSelect={handleCalendarSelect}
            onClose={() => setCalendarOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
